import type { FC } from "react";
import { useEffect } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { fetchPointsHistoryReferral } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";

import { trackCtaClick } from "../../utils/trackCtaClick";
import { HistoryTable } from "../HistoryTable/HistoryTable";

import styles from "./ReferralHistory.module.scss";

export type ReferralHistoryProps = {
  dateLabel: string;
  eventLabel: string;
  pointsLabel: string;
  title: string;
  description?: string;
  rowsCount: number;
  url: string;
  text: string;
  pointsUnit?: string;
  isInEditor?: boolean;
};

export const ReferralHistory: FC<ReferralHistoryProps> = ({
  pointsLabel,
  eventLabel,
  dateLabel,
  pointsUnit,
  title,
  rowsCount = 5,
  url,
  text,
  isInEditor,
  description,
}) => {
  const dispatch = useDispatch();
  const { historyReferral } = useSelector((store: RootStoreState) => store.loyaltyCore);
  const { isReferralOn } = useConfig();
  const labels = { points: pointsLabel, event: eventLabel, date: dateLabel };

  useEffect(() => {
    dispatch(fetchPointsHistoryReferral(rowsCount));
  }, [dispatch, rowsCount]);

  const dispatchGTMEvent = () => {
    trackCtaClick(text, "ReferralHistoryCTA");
  };

  if ((historyReferral.total < 1 || !isReferralOn) && !isInEditor) return null;

  return (
    <div className={styles.referralHistory}>
      <Card title={title} description={description} showSeparator>
        <div className={styles.container}>
          <HistoryTable rows={historyReferral.items} labels={labels} variant={"referral"} pointsUnit={pointsUnit} />
          {historyReferral.total > rowsCount && (
            <div className={styles.linkWrapper}>
              <Button
                variant="text-muted"
                size="sm"
                href={url}
                onClick={dispatchGTMEvent}
                iconAfter={LongArrowForwardIcon}
              >
                {text}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
